import { useQuery, useMutation } from '@tanstack/react-query'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { notificationsApi, notificationSettingsApi } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { toast } from 'sonner'
import type {
  UpdateNotificationConfigRequest,
  CreateRuleRequest,
  UpdateRuleRequest,
} from '../../app/modules/types/notification'

// ============================================
// NOTIFICATION QUERIES
// ============================================

/**
 * Hook for fetching notifications with optional auto-refresh
 * Timing controlled via environment variables
 * @param refetchInterval - Optional interval in milliseconds for auto-refresh (default: from config)
 */
export function useNotifications(refetchInterval?: number | false) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.notifications.list(orgId),
    queryFn: () => notificationsApi.getList(),
    staleTime: QUERY_CONFIG.NOTIFICATIONS.staleTime,
    refetchInterval: refetchInterval !== undefined ? refetchInterval : QUERY_CONFIG.NOTIFICATIONS.refetchInterval || false,
    enabled: !!orgId,
  })
}

// ============================================
// NOTIFICATION MUTATIONS
// ============================================

/**
 * Hook for marking a notification as read
 */
export function useMarkNotificationRead() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (notificationId: string) => notificationsApi.markAsRead(notificationId),
    onSuccess: () => {
      // Invalidate notifications list to refresh unread count
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(orgId) })
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for marking all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(orgId) })
      toast.success('All notifications marked as read')
    },
    onError: (error: any) => {

    },
  })
}

// ============================================
// V2 — CHANNEL CONFIG HOOKS
// ============================================

export function useNotificationConfig() {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.notificationConfig.detail(orgId),
    queryFn: () => notificationSettingsApi.getConfig(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!orgId,
  })
}

export function useUpdateNotificationConfig() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (payload: UpdateNotificationConfigRequest) =>
      notificationSettingsApi.updateConfig(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationConfig.all(orgId) })
      toast.success('Channel configuration saved')
    },
    onError: (error: any) => {

    },
  })
}

export function useTestChannel() {
  return useMutation({
    mutationFn: (channel: string) => notificationSettingsApi.testChannel(channel),
    onSuccess: () => {
      toast.success('Test notification sent')
    },
    onError: (error: any) => {

    },
  })
}

// ============================================
// V2 — EVENT TYPES HOOKS
// ============================================

export function useNotificationEventTypes() {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.notificationEventTypes.list(orgId),
    queryFn: () => notificationSettingsApi.getEventTypes(),
    staleTime: 30 * 60 * 1000,
    enabled: !!orgId,
  })
}

// ============================================
// V2 — RULES HOOKS
// ============================================

export function useNotificationRules() {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.notificationRules.list(orgId),
    queryFn: () => notificationSettingsApi.getRules(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!orgId,
  })
}

export function useCreateNotificationRule() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (payload: CreateRuleRequest) => notificationSettingsApi.createRule(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationRules.all(orgId) })
      toast.success('Rule created successfully')
    },
    onError: (error: any) => {
      const detail = error?.response?.data
      const msg =
        typeof detail === 'string' ? detail : JSON.stringify(detail) || 'Failed to create rule'

    },
  })
}

export function useUpdateNotificationRule() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRuleRequest }) =>
      notificationSettingsApi.updateRule(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationRules.all(orgId) })
      toast.success('Rule updated')
    },
    onError: (error: any) => {

    },
  })
}

export function useDeleteNotificationRule() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (id: string) => notificationSettingsApi.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationRules.all(orgId) })
      toast.success('Rule deleted')
    },
    onError: (error: any) => {

    },
  })
}

export function useToggleNotificationRule() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (id: string) => notificationSettingsApi.toggleRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationRules.all(orgId) })
    },
    onError: (error: any) => {

    },
  })
}

// ============================================
// NOTIFICATION DETAIL & UNREAD COUNT
// ============================================

export function useNotificationDetail(notificationId: string) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: [...queryKeys.notifications.all(orgId), 'detail', notificationId],
    queryFn: () => notificationsApi.getDetail(notificationId),
    enabled: !!orgId && !!notificationId,
  })
}

export function useNotificationUnreadCount() {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: [...queryKeys.notifications.all(orgId), 'unreadCount'],
    queryFn: () => notificationsApi.getUnreadCount(),
    staleTime: QUERY_CONFIG.NOTIFICATIONS.staleTime,
    refetchInterval: QUERY_CONFIG.NOTIFICATIONS.refetchInterval || false,
    enabled: !!orgId,
  })
}

export function useMarkGroupNotificationsRead() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (groupKey: string) => notificationsApi.markGroupAsRead(groupKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(orgId) })
    },
    onError: (error: any) => {

    },
  })
}

// ============================================
// V2 — RULE LOGS
// ============================================

export function useNotificationRuleLogs(ruleId: string) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.notificationRules.logs(orgId, ruleId),
    queryFn: () => notificationSettingsApi.getRuleLogs(ruleId),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!orgId && !!ruleId,
  })
}

export function useSendTestNotification() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: () => notificationsApi.sendTestNotification(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(orgId) })
      toast.success('Test notification sent')
    },
    onError: (error: any) => {

    },
  })
}
